import api from './api';
import {
  CreateGroupDto,
  CreateLocationsWithGroups,
  GroupDto,
  KindergartenDetailsDto,
  KindergartenDto,
  LocationDto,
  UpdateLocation,
  TeacherDto,
  GroupWithLocationName,
  CreateTeacherDto,
  TeacherDetailsDto,
  UpdateTeacherDto,
  ChildDto,
  CreateChildDto,
  ParentDto,
  UpdateChildDto,
  CreateParentDto,
  UpdateParentDto,
  UserBasicInfo,
  ChildBasicInfo,
  KindergartenBasicInfoDto,
  ImportDto
} from '../common/interfaces/kindergarten.interface';
import { ApiResponseDto } from '../common/interfaces/common.interface';
import qs from 'qs';
import { CreateImportUserTypeEnum, GeneralStatusEnum, SortOrderEnum } from '../common/enums/common.enum';
import { ChildrenTabSortKeyEnum, ParentsTabSortKeyEnum, TeachersTabSortKeyEnum } from '../common/enums/kindergarten.enum';

export const getAdminKindergartens = async (page: number, perPage: number, search?: string): Promise<ApiResponseDto<KindergartenDto[]>> => {
  const params: any = { page, perPage };
  if (search) params.search = search;

  const response = await api.get(`/kindergarten/list`, { params });
  return response.data;
};

export const getKindergartenDetails = async (kindergartenId: string): Promise<ApiResponseDto<KindergartenDetailsDto>> => {
  const response = await api.get(`/kindergarten/${kindergartenId}/details`);
  return response.data;
};

export const getLocationsWIthGroups = async (
  kindergartenId: string,
  status?: GeneralStatusEnum
): Promise<ApiResponseDto<LocationDto[]>> => {
  const response = await api.get(`/kindergarten/${kindergartenId}/locations`, { params: { status } });
  return response.data;
};

export const createGroup = async (
  kindergartenId: string,
  locationId: string,
  createGroupDto: CreateGroupDto
): Promise<ApiResponseDto<GroupDto>> => {
  const response = await api.post(`kindergarten/${kindergartenId}/group/${locationId}`, createGroupDto);
  return response.data;
};

export const updateGroup = async (
  kindergartenId: string,
  groupId: string,
  updateGroupDto: CreateGroupDto
): Promise<ApiResponseDto<GroupDto>> => {
  const response = await api.patch(`kindergarten/${kindergartenId}/group/${groupId}`, updateGroupDto);
  return response.data;
};

export const createLocations = async (kindergartenId: string, locationsDto: CreateLocationsWithGroups[]): Promise<ApiResponseDto<void>> => {
  const response = await api.post(`/kindergarten/${kindergartenId}/locations`, {
    locations: locationsDto
  });
  return response.data;
};

export const updateLocation = async (
  kindergartenId: string,
  locationId: string,
  updateLocationDto: UpdateLocation
): Promise<ApiResponseDto<void>> => {
  const response = await api.patch(`/kindergarten/${kindergartenId}/location/${locationId}`, updateLocationDto);

  return response.data;
};

export const fetchGroupDetails = async (kindergartenId: string, groupId: string): Promise<ApiResponseDto<void>> => {
  const response = await api.get(`/kindergarten/${kindergartenId}/group/${groupId}/details`);

  return response.data;
};

export const fetchTeachers = async (
  kindergartenId: string,
  page: number,
  perPage: number,
  sortBy?: TeachersTabSortKeyEnum,
  sortOrder?: SortOrderEnum,
  name?: string,
  groupIds?: string[]
): Promise<ApiResponseDto<TeacherDto[]>> => {
  const params: any = {
    page,
    perPage,
    sortBy,
    sortOrder,
    name,
    groupIds
  };

  if (name) {
    params.name = name;
  }

  if (groupIds?.length) {
    params.groupIds = groupIds;
  }

  const response = await api.get(`user/teachers/kindergarten/${kindergartenId}`, {
    params,
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' })
  });

  return response.data;
};

export const getGroupsWithLocationName = async (kindergartenId: string): Promise<ApiResponseDto<GroupWithLocationName[]>> => {
  const response = await api.get(`/kindergarten/${kindergartenId}/groups`);
  return response.data;
};

export const createTeacher = async (kindergartenId: string, teacherDto: CreateTeacherDto): Promise<ApiResponseDto<void>> => {
  const response = await api.post(`/user/teacher/${kindergartenId}`, teacherDto);
  return response.data;
};

export const getTeacherDetails = async (kindergartenId: string, teacherId: string): Promise<ApiResponseDto<TeacherDetailsDto>> => {
  const response = await api.get(`/user/teacher/${teacherId}/kindergarten/${kindergartenId}`);
  return response.data;
};

export const updateTeacher = async (
  kindergartenId: string,
  teacherId: string,
  updateTeacherDto: UpdateTeacherDto
): Promise<ApiResponseDto<void>> => {
  const response = await api.patch(`/user/teacher/${teacherId}/update/kindergarten/${kindergartenId}`, updateTeacherDto);
  return response.data;
};

export const updateTeacherStatus = async (kindergartenId: string, teacherId: string): Promise<ApiResponseDto<void>> => {
  const response = await api.patch(`/user/teacher/${teacherId}/change-status/kindergarten/${kindergartenId}`);
  return response.data;
};

export const fetchChildren = async (
  kindergartenId: string,
  page: number,
  perPage: number,
  sortBy?: ChildrenTabSortKeyEnum,
  sortOrder?: SortOrderEnum,
  name?: string,
  groupIds?: string[],
  withParents?: boolean
): Promise<ApiResponseDto<ChildDto[]>> => {
  const params: any = {
    page,
    perPage,
    sortBy,
    sortOrder,
    name,
    groupIds,
    withParents
  };
  if (name) {
    params.name = name;
  }
  if (groupIds?.length) {
    params.groupIds = groupIds;
  }
  if (withParents !== undefined) {
    params.withParents = withParents;
  }

  const response = await api.get(`child/list/${kindergartenId}`, {
    params,
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' })
  });

  return response.data;
};

export const updateChildStatus = async (kindergartenId: string, childId: string): Promise<ApiResponseDto<void>> => {
  const response = await api.patch(`/child/${childId}/change-status/kindergarten/${kindergartenId}`);
  return response.data;
};

export const createChild = async (kindergartenId: string, childDto: CreateChildDto): Promise<ApiResponseDto<void>> => {
  const response = await api.post(`/child/${kindergartenId}`, childDto);
  return response.data;
};

export const getChildDetails = async (kindergartenId: string, childId: string): Promise<ApiResponseDto<ChildDto>> => {
  const response = await api.get(`/child/${childId}/details/admin/kindergarten/${kindergartenId}`);
  return response.data;
};

export const updateChild = async (
  kindergartenId: string,
  childId: string,
  updateChildDto: UpdateChildDto
): Promise<ApiResponseDto<void>> => {
  const response = await api.patch(`/child/${childId}/update/kindergarten/${kindergartenId}`, updateChildDto);
  return response.data;
};

export const fetchParents = async (
  kindergartenId: string,
  page: number,
  perPage: number,
  sortBy?: ParentsTabSortKeyEnum,
  sortOrder?: SortOrderEnum,
  name?: string,
  withChildren?: boolean
): Promise<ApiResponseDto<ParentDto[]>> => {
  const params: any = {
    page,
    perPage,
    sortBy,
    sortOrder,
    name,
    withChildren
  };

  if (name) {
    params.name = name;
  }

  if (withChildren !== undefined) {
    params.withChildren = withChildren;
  }

  const response = await api.get(`user/parents/kindergarten/${kindergartenId}`, {
    params,
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' })
  });

  return response.data;
};

export const updateParentStatus = async (kindergartenId: string, parentId: string): Promise<ApiResponseDto<void>> => {
  const response = await api.patch(`/user/parent/${parentId}/change-status/kindergarten/${kindergartenId}`);
  return response.data;
};

export const getParentDetails = async (kindergartenId: string, parentId: string): Promise<ApiResponseDto<ParentDto>> => {
  const response = await api.get(`/user/parent/${parentId}/kindergarten/${kindergartenId}`);
  return response.data;
};

export const createParent = async (kindergartenId: string, parentDto: CreateParentDto): Promise<ApiResponseDto<void>> => {
  const response = await api.post(`/user/parent/${kindergartenId}`, parentDto);
  return response.data;
};

export const updateParent = async (
  kindergartenId: string,
  parentId: string,
  updateParentDto: UpdateParentDto
): Promise<ApiResponseDto<void>> => {
  const response = await api.patch(`/user/parent/${parentId}/update/kindergarten/${kindergartenId}`, updateParentDto);
  return response.data;
};

export const fetchParentsBasicInfo = async (kindergartenId: string): Promise<ApiResponseDto<UserBasicInfo[]>> => {
  const response = await api.get(`user/parents/basic-info/kindergarten/${kindergartenId}`);
  return response.data;
};

export const fetchChildrenBasicInfo = async (kindergartenId: string): Promise<ApiResponseDto<ChildBasicInfo[]>> => {
  const response = await api.get(`child/list/basic-info/kindergarten/${kindergartenId}`);
  return response.data;
};

export const getKindergartenLocationsAndGroups = async (options?: {
  kindergartenIds?: string[];
  name?: string;
}): Promise<ApiResponseDto<KindergartenDto[]>> => {
  const params: any = {};

  if (options?.kindergartenIds?.length) {
    params.kindergartenIds = options.kindergartenIds;
  }
  if (options?.name) {
    params.name = options.name;
  }

  const response = await api.get(`/kindergarten/group/list`, {
    params,
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' })
  });
  return response.data;
};

export const getActiveKindergartensForAdmin = async (search?: string): Promise<ApiResponseDto<KindergartenBasicInfoDto[]>> => {
  const params: any = {};
  if (search) params.search = search;

  const response = await api.get(`/kindergarten/list/active`, { params });
  return response.data;
};

export const importChildren = async (file: File, kindergartenId: string): Promise<ApiResponseDto<ImportDto[]>> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post(`/child/import/${kindergartenId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const importUsers = async (
  file: File,
  kindergartenId: string,
  userType: CreateImportUserTypeEnum
): Promise<ApiResponseDto<ImportDto[]>> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('userType', userType);

  const response = await api.post(`/user/import/${kindergartenId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};
